/**
 * Pure functions of the `hidden_article` type: the progress blob, the submission, how a
 * server verdict folds back into the progress, and the reading of the skeleton (ADR 0042).
 * No component here, so `module.ts` stays light in the eager bundle.
 */
import type { Verdict } from "../types";
import {
  EXACT,
  type Hit,
  type HiddenArticleDefinition,
  type HiddenArticleProgress,
  type HiddenArticleSubmission,
  type Token,
} from "./types";

/** Mirrors the server's `normalize_guess`: NFC, surrounding spaces removed, lowercase. */
export function normalizeGuess(word: string): string {
  return word.normalize("NFC").trim().toLowerCase();
}

/** Mirrors the server's `fold`: how two words are compared — no case, no accent. */
export function foldWord(word: string): string {
  return normalizeGuess(word).normalize("NFD").replace(/\p{M}/gu, "").normalize("NFC");
}

export function emptyDefinition(): HiddenArticleDefinition {
  return {
    puzzleType: "hidden_article",
    typeVersion: 1,
    language: "fr",
    title: [],
    paragraphs: [],
    rulesText: {
      fr: "Un article de Wikipédia est caché : chaque mot est remplacé par un rectangle de sa longueur. Proposez des mots. Un mot présent dans l'article se dévoile partout où il apparaît, avec ses formes au pluriel et au féminin (le, la, les) ; un mot proche s'affiche en couleur à la place du mot caché, sans le dévoiler. Vous avez gagné quand le titre est entièrement dévoilé.",
      en: "A Wikipedia article is hidden: every word is replaced by a bar of its length. Guess words. A word that is in the article shows up wherever it appears, along with its plural (and forms such as he, him, his); a close word is displayed in colour in place of the hidden one, without revealing it. You win once the title is fully revealed.",
    },
  };
}

export function emptyProgress(_definition: HiddenArticleDefinition): HiddenArticleProgress {
  return { version: 1, draft: "", guesses: [], hits: [] };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asHit(value: unknown): Hit | null {
  if (!Array.isArray(value) || (value.length !== 3 && value.length !== 4)) return null;
  const [index, guess, similarity, form] = value;
  if (![index, guess, similarity].every((n) => typeof n === "number" && Number.isInteger(n)))
    return null;
  if (index < 0 || guess < 0 || similarity < 0 || similarity > EXACT) return null;
  if (value.length === 3) return [index, guess, similarity];
  if (typeof form !== "string" || !form || similarity !== EXACT) return null;
  return [index, guess, similarity, form];
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(
  definition: HiddenArticleDefinition,
  raw: unknown,
): HiddenArticleProgress {
  if (!isRecord(raw)) return emptyProgress(definition);
  const guesses = Array.isArray(raw.guesses)
    ? raw.guesses.filter((g): g is string => typeof g === "string")
    : [];
  const hits = Array.isArray(raw.hits)
    ? raw.hits.map(asHit).filter((h): h is Hit => h !== null && h[1] < guesses.length)
    : [];
  const progress: HiddenArticleProgress = {
    version: 1,
    draft: typeof raw.draft === "string" ? raw.draft : "",
    guesses,
    hits,
  };
  if (typeof raw.titleFound === "number") progress.titleFound = raw.titleFound;
  if (raw.found === true) progress.found = true;
  return progress;
}

/** The guess already made with this word, if any: the Player refuses it locally. */
export function findGuess(progress: HiddenArticleProgress, word: string): string | undefined {
  const wanted = foldWord(word);
  return progress.guesses.find((guess) => foldWord(guess) === wanted);
}

/**
 * Every guess so far plus the draft: the verdict is a function of the whole list, so a
 * teammate's guesses (shared through the progress) count towards the same page.
 */
export function serializeSubmission(
  _definition: HiddenArticleDefinition,
  progress: HiddenArticleProgress,
): HiddenArticleSubmission {
  const draft = normalizeGuess(progress.draft);
  if (!draft || findGuess(progress, draft)) return { guesses: [...progress.guesses] };
  return { guesses: [...progress.guesses, draft] };
}

/**
 * Folds one verdict into the progress: the server's normalised guess list replaces ours
 * (so indices in the hits line up), the hits are taken whole, the draft is cleared. A verdict
 * without a reveal (an empty or malformed submission) changes nothing.
 */
export function applyVerdict(
  _definition: HiddenArticleDefinition,
  progress: HiddenArticleProgress,
  verdict: Verdict<HiddenArticleSubmission>,
): HiddenArticleProgress {
  const reveal = verdict.feedback.find((item) => item.code === "reveal");
  if (!reveal) return progress;
  const params = reveal.params ?? {};
  const guesses = Array.isArray(params.guesses)
    ? params.guesses.filter((g): g is string => typeof g === "string")
    : verdict.submission.guesses.map(normalizeGuess);
  const hits = Array.isArray(params.hits)
    ? params.hits.map(asHit).filter((h): h is Hit => h !== null)
    : [];
  const next: HiddenArticleProgress = { version: 1, draft: "", guesses, hits };
  if (typeof params.title_found === "number") next.titleFound = params.title_found;
  if (verdict.correct) next.found = true;
  return next;
}

// ---- reading the skeleton ---------------------------------------------------------------

/** A token with its hidden index (title first, then paragraphs), or null for plain text. */
export interface NumberedToken {
  token: Token;
  index: number | null;
}

export interface NumberedArticle {
  title: NumberedToken[];
  paragraphs: { heading: boolean; tokens: NumberedToken[] }[];
  /** Hidden words in total. */
  hidden: number;
  /** Hidden words in the title. */
  titleHidden: number;
}

/** Numbers the hidden words the way the server and the solution do. */
export function numberArticle(definition: HiddenArticleDefinition): NumberedArticle {
  let index = 0;
  const number = (tokens: Token[]): NumberedToken[] =>
    tokens.map((token) => ({ token, index: token.kind === "hidden" ? index++ : null }));
  const title = number(definition.title ?? []);
  const titleHidden = index;
  const paragraphs = (definition.paragraphs ?? []).map((paragraph) => ({
    heading: paragraph.heading === true,
    tokens: number(paragraph.tokens ?? []),
  }));
  return { title, paragraphs, hidden: index, titleHidden };
}

export interface HitInfo {
  guess: number;
  similarity: number;
  /** The page's spelling, when the guess revealed an inflected form of itself. */
  form?: string;
}

export function hitMap(progress: HiddenArticleProgress): Map<number, HitInfo> {
  const map = new Map<number, HitInfo>();
  for (const [index, guess, similarity, form] of progress.hits) {
    map.set(index, form === undefined ? { guess, similarity } : { guess, similarity, form });
  }
  return map;
}

export type Heat = "found" | "hot" | "warm";

/** A band for the eye: the word itself, a near neighbour, or merely a related word. */
export function heatOf(similarity: number): Heat {
  if (similarity >= EXACT) return "found";
  if (similarity >= 70) return "hot";
  return "warm";
}

export interface GuessStats {
  /** Positions this guess revealed. */
  exact: number;
  /** Positions this guess is shown next to as a close word. */
  close: number;
}

/** Per guess, what it did on the page: how many words it revealed, how many it stands near. */
export function guessStats(progress: HiddenArticleProgress): GuessStats[] {
  const stats = progress.guesses.map(() => ({ exact: 0, close: 0 }));
  for (const [, guess, similarity] of progress.hits) {
    const entry = stats[guess];
    if (!entry) continue;
    if (similarity >= EXACT) entry.exact += 1;
    else entry.close += 1;
  }
  return stats;
}

/** Hidden words revealed so far. */
export function revealedCount(progress: HiddenArticleProgress): number {
  return progress.hits.filter(([, , similarity]) => similarity >= EXACT).length;
}

/**
 * The page's title as far as it is known, for the link to the article once found: revealed
 * words in the player's own spelling, plain text as-is. Empty while a word is missing.
 */
export function knownTitle(
  definition: HiddenArticleDefinition,
  progress: HiddenArticleProgress,
): string | null {
  const hits = hitMap(progress);
  const parts: string[] = [];
  for (const { token, index } of numberArticle(definition).title) {
    if (token.kind === "text") {
      parts.push(token.text);
      continue;
    }
    const hit = index === null ? undefined : hits.get(index);
    if (!hit || hit.similarity < EXACT) return null;
    parts.push(hit.form ?? progress.guesses[hit.guess] ?? "");
  }
  const title = parts.join("").trim();
  return title || null;
}

/** Where to read the page once found: a title search, which lands on the article whatever
 * the case the player typed in. */
export function articleSearchUrl(language: string, title: string): string {
  return `https://${language}.wikipedia.org/w/index.php?${new URLSearchParams({ search: title, go: "Go" }).toString()}`;
}
